import { Link as RouterLink } from "react-router-dom";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { SaleDepositCreditDebitNoteAdjustment } from "../../types/sale-deposit.type";
import { creditDebitNoteDetailPath } from "../CreditDebitNote/constants";
import { formatSignedBaht } from "../CreditDebitNote/reportFigures";

interface NoteAdjustmentLinesProps {
  adjustment: SaleDepositCreditDebitNoteAdjustment | undefined;
}

/** The same link look as a bill number in the sales list. */
const NOTE_LINK_SX = { color: "link.main" } as const;

/** An amount never breaks across lines: the minus would end up alone. */
const NO_WRAP_SX = { whiteSpace: "nowrap" } as const;

/** A credit note hands money back, so it reads in the critical colour; a debit note is plain. */
const amountColor = (amount: number) => (amount < 0 ? "error" : "text.primary");

/**
 * The cash ใบลดหนี้ / ใบเพิ่มหนี้ already inside a route-day's ที่ต้องโอน: one line per note type
 * with its amount, and under it each note's number as a link to the note. Nothing is drawn when
 * the day has none, so a deposit without notes reads as it always did.
 *
 * The notes open in a new tab so a form or a filtered list being worked on is not lost.
 */
export default function NoteAdjustmentLines({
  adjustment,
}: Readonly<NoteAdjustmentLinesProps>) {
  if (!adjustment || adjustment.lines.length === 0) return null;

  return (
    <Stack gap={1}>
      {adjustment.lines.map((line) => (
        <Stack key={line.note_type}>
          <Stack direction="row" justifyContent="space-between" gap={2}>
            <Typography variant="body2" fontWeight={600}>
              {line.label}
            </Typography>
            <Typography
              variant="body2"
              fontWeight={700}
              color={amountColor(line.amount)}
              sx={NO_WRAP_SX}
            >
              {formatSignedBaht(line.amount)}
            </Typography>
          </Stack>
          {line.notes.map((note) => (
            <Stack
              key={note.note_uuid}
              direction="row"
              justifyContent="space-between"
              gap={2}
              pl={2}
            >
              <Link
                component={RouterLink}
                to={creditDebitNoteDetailPath(note.note_uuid)}
                target="_blank"
                rel="noreferrer"
                variant="caption"
                sx={NOTE_LINK_SX}
              >
                {note.note_no}
              </Link>
              <Typography
                variant="caption"
                color={amountColor(note.amount)}
                sx={NO_WRAP_SX}
              >
                {formatSignedBaht(note.amount)}
              </Typography>
            </Stack>
          ))}
        </Stack>
      ))}
    </Stack>
  );
}
